import { Navigate, useParams } from 'react-router';
import { DocsContent } from '@/components/docs/docs-content';
import { DocsRouteContext } from '@/components/docs/docs-route-context';
import { findPage } from '@/components/docs/docs-manifest';
import NotFoundPage from '@/pages/not-found';

const docsModules = import.meta.glob('../content/docs/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function extractDescription(source: string): string {
  const lines = source.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('#')) continue;
    if (trimmed.startsWith('>')) continue;
    if (trimmed.startsWith('![')) continue;
    if (trimmed.startsWith('-') || trimmed.startsWith('*') || trimmed.startsWith('|')) continue;
    // Strip markdown formatting
    return trimmed.replace(/[*_`]/g, '').slice(0, 160);
  }
  return 'Practice Seeds documentation.';
}

function extractFirstImageSrc(source: string, role: string): string | undefined {
  const match = source.match(/!\[[^\]]*\]\(([^)]+)\)/);
  if (!match) return undefined;
  const src = match[1];
  if (src.startsWith('http')) return src;
  const normalized = src.startsWith('./') ? src.slice(2) : src;
  return `/docs/${role}/${normalized}`;
}

export default function DocsPage() {
  const { role, page } = useParams();
  if (!role) return <Navigate to="/docs" replace />;

  const pageSlug = page ?? 'README';
  const key = `../content/docs/${role}/${pageSlug}.md`;
  const source = docsModules[key];

  if (!source) {
    return <NotFoundPage />;
  }

  const pageEntry = findPage(role, pageSlug);
  const title = pageEntry?.title ?? 'Documentation';
  const description = extractDescription(source);
  const ogImage = extractFirstImageSrc(source, role);
  const canonical = `/docs/${role}/${pageSlug}`;

  return (
    <DocsRouteContext.Provider value={{ role, pageSlug }}>
      <title>{`${title} · Practice Seeds Docs`}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <DocsContent source={source} />
    </DocsRouteContext.Provider>
  );
}
